import { useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import { ApiOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayGioVN, ngayVN, soVN } from '../../../lib/dinh-dang';
import { useDanhMucQuyen } from '../../vai-tro/hooks/useVaiTro';
import { BADGE_TRANG_THAI, SO_MODULE_HIEN, TONG_MODULE } from '../constants';
import { useDanhSachKhoaApi } from '../hooks/useKhoaApi';
import { useThaoTacKhoaApi } from '../hooks/useThaoTacKhoaApi';
import { locKhoa, moduleCuaKhoa, thuHoiDuoc } from '../nghiep-vu/khoa-api';
import type { ApiKey, ApiKeyStatus } from '../types';

type LocTrangThai = ApiKeyStatus | 'tat-ca';

/**
 * Khoá API của tổ chức (cổng tenant, `/he-thong/khoa-api`, G2-D1-18, ADR-0068):
 * khoá cho hệ thống bên ngoài gọi API của module qua Gateway bằng header
 * `X-Api-Key`. Tạo (bí mật hiện một lần), xem lần dùng cuối, thu hồi. Chỉ quản
 * trị tổ chức vào được (phân hệ `API_KEY`, dịch vụ `cau-hinh-he-thong`).
 *
 * Tải hết danh sách rồi lọc ở client theo trạng thái và tên.
 */
export function KhoaApiView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const [tuKhoa, setTuKhoa] = useState('');
  const [trangThai, setTrangThai] = useState<LocTrangThai>('tat-ca');
  const q = useDanhSachKhoaApi();
  // Chỉ để đổi mã module thành tên trong cột Quyền; lỗi thì lùi về mã, không chặn bảng.
  const danhMuc = useDanhMucQuyen();
  const thaoTac = useThaoTacKhoaApi();
  const tieuDe = t('nav.muc.khoaApiToChuc');

  const tenModule = useMemo(
    () => new Map((danhMuc.data ?? []).map((p) => [p.module_code, p.module_name])),
    [danhMuc.data],
  );
  const tatCa = q.data ?? [];
  const dong = useMemo(() => locKhoa(q.data ?? [], trangThai, tuKhoa), [q.data, trangThai, tuKhoa]);
  const coLoc = tuKhoa.trim() !== '' || trangThai !== 'tat-ca';
  const dangTai = q.isLoading;

  const columns: TableProps<ApiKey>['columns'] = [
    {
      title: t('khoaApi.cotKhoa'),
      width: 280,
      render: (_v, row) => (
        <div className="flex min-w-0 max-w-xs flex-col">
          <span className="truncate text-sm font-medium text-content-primary" title={row.name}>
            {row.name}
          </span>
          <span className="truncate font-mono text-2xs text-content-muted">
            {t('khoaApi.tienToHien', { tienTo: row.key_prefix })}
          </span>
        </div>
      ),
    },
    {
      title: t('khoaApi.cotQuyen'),
      width: 280,
      render: (_v, row) => {
        const modules = moduleCuaKhoa(row.scopes);
        const conLai = modules.length - SO_MODULE_HIEN;
        return (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-xs text-content-secondary">
              {t('khoaApi.soQuyen', { so: soVN(row.scopes.length, locale) })}
            </span>
            <span className="flex flex-wrap gap-1">
              {modules.slice(0, SO_MODULE_HIEN).map((m) => (
                <Badge key={m} variant={TONG_MODULE}>
                  {tenModule.get(m) ?? m}
                </Badge>
              ))}
              {conLai > 0 && (
                <span
                  className="text-2xs text-content-muted"
                  title={modules.slice(SO_MODULE_HIEN).map((m) => tenModule.get(m) ?? m).join(', ')}
                >
                  {t('khoaApi.themModule', { so: conLai })}
                </span>
              )}
            </span>
          </div>
        );
      },
    },
    {
      title: t('khoaApi.cotTrangThai'),
      width: 130,
      render: (_v, row) => (
        <Badge variant={BADGE_TRANG_THAI[row.status].variant}>{t(BADGE_TRANG_THAI[row.status].khoa)}</Badge>
      ),
    },
    {
      title: t('khoaApi.cotHetHan'),
      dataIndex: 'expires_at',
      width: 120,
      render: (value: string | null) => (
        <span className="whitespace-nowrap text-xs text-content-muted">
          {value ? ngayVN(value) : t('khoaApi.khongHetHan')}
        </span>
      ),
    },
    {
      title: t('khoaApi.cotDungLanCuoi'),
      dataIndex: 'last_used_at',
      width: 150,
      render: (value: string | null) =>
        value ? (
          <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
            {ngayGioVN(value, locale)}
          </span>
        ) : (
          <span className="text-xs text-content-muted">{t('khoaApi.chuaDung')}</span>
        ),
    },
    {
      title: t('khoaApi.cotTaoLuc'),
      dataIndex: 'created_at',
      width: 110,
      render: (value: string) => (
        <span className="whitespace-nowrap text-xs text-content-muted">{ngayVN(value)}</span>
      ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) => (
        <MenuThaoTac
          tenBanGhi={row.name}
          muc={[
            {
              khoa: 'thu-hoi',
              nhan: t('khoaApi.thuHoiKhoa'),
              icon: <StopOutlined />,
              nhom: 'pha-huy',
              hien: thuHoiDuoc(row),
              onChon: () => thaoTac.moThuHoi(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.cauHinhHeThong') },
            { label: t('nav.muc.cauHinhHeThong') },
            { label: tieuDe },
          ]}
          titleIcon={<ApiOutlined className="text-primary-600" />}
          title={tieuDe}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void q.refetch()}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={thaoTac.moTao}>
                {t('khoaApi.taoKhoa')}
              </Button>
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            allowClear
            className="w-full sm:w-80"
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('khoaApi.timPlaceholder')}
            aria-label={t('khoaApi.timNhan')}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
          <Select<LocTrangThai>
            className="w-full sm:w-48"
            aria-label={t('khoaApi.locTrangThai')}
            value={trangThai}
            onChange={setTrangThai}
            options={[
              { value: 'tat-ca', label: t('khoaApi.tatCaTrangThai') },
              ...(Object.keys(BADGE_TRANG_THAI) as ApiKeyStatus[]).map((s) => ({
                value: s,
                label: t(BADGE_TRANG_THAI[s].khoa),
              })),
            ]}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {q.error && !q.data ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">
                {t('khoaApi.loiDanhSach', { loi: dichLoi(q.error) })}
              </span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <>
              <DataTable<ApiKey>
                caption={t('khoaApi.bangCaption')}
                rowKey="id"
                dataSource={dong}
                columns={columns}
                loading={dangTai}
                size="middle"
                pagination={false}
                locale={{
                  emptyText: dangTai ? (
                    ' '
                  ) : (
                    <Empty
                      icon={ApiOutlined}
                      // Rỗng vì bộ lọc: bỏ mô tả và lời mời hành động.
                      title={coLoc ? t('khoaApi.rongLoc') : t('khoaApi.rongThat')}
                      description={coLoc ? undefined : t('khoaApi.rongThatMo')}
                      action={
                        coLoc ? undefined : (
                          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={thaoTac.moTao}>
                            {t('khoaApi.taoKhoaDauTien')}
                          </Button>
                        )
                      }
                    />
                  ),
                }}
              />
              {dong.length > 0 && (
                <div className="border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted" role="status">
                    {coLoc
                      ? t('khoaApi.dangHienLoc', { so: soVN(dong.length, locale), tong: soVN(tatCa.length, locale) })
                      : t('khoaApi.dangHien', { so: soVN(dong.length, locale) })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {thaoTac.hopThoai}
    </div>
  );
}
